import { getResponseErrorDetails } from './error';

type LogFields = Record<string, unknown>;

export function getErrorLogFields(error: unknown): LogFields {
  if (error instanceof Error) {
    return {
      error_message: error.message,
      error_name: error.name,
      error_stack: error.stack,
    };
  }

  if (error !== null && typeof error === 'object') {
    const details = getResponseErrorDetails(error);

    return {
      error_code: details.code,
      error_message: details.message,
      status: details.status,
    };
  }

  return {
    error_message: String(error),
  };
}

export function logStructuredEvent(event: string, fields: LogFields = {}) {
  console.log(JSON.stringify({
    event,
    level: 'info',
    timestamp: new Date().toISOString(),
    ...fields,
  }));
}

export function logStructuredError(event: string, fields: LogFields = {}) {
  console.error(JSON.stringify({
    event,
    level: 'error',
    timestamp: new Date().toISOString(),
    ...fields,
  }));
}

export interface ServerActionLogParams {
  action: string;
  /** Log-event namespace for the calling app, e.g. `dashboard` or `bridge_admin`. */
  app: string;
  error?: unknown;
  /**
   * App-specific identity fields for log correlation (e.g. `developer_id`, `session_id`).
   * Must never carry a bearer credential such as a session cookie.
   */
  identity?: LogFields;
  method: string;
  path: string;
  startedAt: number;
  status: number;
  success: boolean;
}

/**
 * Emits exactly one `<app>.server_action` log per server-action request.
 *
 * Successes and 4xx failures log at `info` level: a 4xx is a client error, so only the
 * upstream error message is attached — the rest of its outcome is described by
 * `success` and `http_request.response_code`. Any other failure is a server error and
 * logs at `error` level with the error's full details.
 */
export function logServerAction({
  action,
  app,
  error,
  identity,
  method,
  path,
  startedAt,
  status,
  success,
}: ServerActionLogParams) {
  const baseFields = {
    action,
    duration_ms: Date.now() - startedAt,
    http_request: {
      method,
      path,
      response_code: status,
    },
    success,
    ...identity,
  };

  const isClientError = status >= 400 && status < 500;

  if (success) {
    logStructuredEvent(`${app}.server_action`, baseFields);
    return;
  }

  if (isClientError) {
    logStructuredEvent(`${app}.server_action`, {
      error_message: getErrorLogFields(error).error_message,
      ...baseFields,
    });
    return;
  }

  // `baseFields` is spread last so the HTTP-level fields we responded with win over any
  // conflicting keys the error payload carries.
  logStructuredError(`${app}.server_action`, {
    ...getErrorLogFields(error),
    ...baseFields,
    source: 'server_action',
  });
}
